import {
  ClientErrorResponse,
  ClientResponse,
  GetVerificationCodesResponse,
  InProgressResponse,
  RequestACallClient,
  RequestACallClientUrls,
  RequestACallParams,
} from './types';
import {
  Analytics,
  Errors,
  initObservability,
  Metrics,
  Observability,
  ObservabilityConfig,
} from './observability';
import {defaultClientFetch} from './clientFetch';

const CLIENT_TYPE = 'request_a_call';
const METRIC_TAGS = {client_type: CLIENT_TYPE};
const ERROR_TAGS = METRIC_TAGS;

// FIXME: Remove this; this should not be the concern of this package.
export class DefaultRequestACallClient implements RequestACallClient {
  protected clientFetch: typeof fetch;

  protected urls?: Partial<RequestACallClientUrls>;

  protected observability: Observability;

  constructor({
    clientFetch = defaultClientFetch,
    urls,
    observability,
  }: {
    clientFetch?: typeof fetch;
    urls?: Partial<RequestACallClientUrls>;
    observability?: ObservabilityConfig;
  } = {}) {
    this.clientFetch = clientFetch;
    this.urls = urls;
    this.observability = initObservability(observability);
  }

  get analytics(): Analytics {
    return this.observability.analytics;
  }

  get errors(): Errors {
    return this.observability.errors;
  }

  get metrics(): Metrics {
    return this.observability.metrics;
  }

  async requestACall(params: RequestACallParams): Promise<ClientResponse> {
    if (!this.urls?.requestACallUrl) {
      throw new Error('missing request a call url');
    }

    const eventId = await this.analytics.requestAnalyticsEventId();

    const resp = await this.fetch(this.urls.requestACallUrl, {
      method: 'POST',
      body: JSON.stringify({
        ...params,
        analyticsEventId: eventId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const json = await resp.json();

    if (json.type === 'success') {
      return {code: 200, data: json};
    }

    return this.error(resp.status, json.type);
  }

  async getVerificationCodes(): Promise<GetVerificationCodesResponse> {
    if (!this.urls?.verificationCodesUrl) {
      throw new Error('missing verification codes url');
    }

    const resp = await this.fetch(this.urls?.verificationCodesUrl);
    const json = await resp.json();

    if (json.type === 'success') {
      const {
        userCode: code,
        agentCode: challengeCode,
        nextReload: duration,
      } = json;
      return {
        code: 200,
        data: {
          code,
          challengeCode,
          duration,
        },
      };
    }

    return this.error(resp.status, json.type);
  }

  async inProgress(): Promise<InProgressResponse> {
    if (!this.urls?.channelsUrl) {
      throw new Error('missing channels url');
    }

    try {
      const resp = await this.fetch(this.urls.channelsUrl);
      const json = await resp.json();

      if (json.type !== 'success') {
        return {code: 200, data: {inProgress: false}};
      }

      const {channels = []} = json;
      const inProgress = channels.some(
        (channel: {type: string; pending?: boolean}) =>
          channel.type === 'request_a_call' && channel.pending,
      );
      return {code: 200, data: {inProgress}};
    } catch (e) {
      return this.error(400, (e as Error).message);
    }
  }

  protected error = async (
    code: number,
    error: string,
  ): Promise<ClientErrorResponse> => {
    if (code >= 500) {
      this.errors.reportError(error, {tags: ERROR_TAGS});
    }
    return Promise.resolve({code, error});
  };

  protected fetch = (url: RequestInfo | URL, init?: RequestInit) => {
    return this.clientFetch(url, init);
  };
}
